import { useMemo, useRef } from 'react';
import { findParentByVariantSku } from '../product-search-rows';
import type { Product, Variant } from '../types/product';
import { useSearchProducts } from './use-search-products';

// Resolves a variant SKU, usually straight from the URL, to its parent product and variant.
// Selecting a variant within the page navigates to a new URL; the parent is cached so switching
// between variants of the same product doesn't refetch the search results.
export function useVariantLookup(sku: string | undefined) {
  const query = sku?.trim() ? sku.trim() : null;

  const cachedParent = useRef<Product | null>(null);
  const hasCachedParent =
    cachedParent.current?.variants.some((v) => v.identifiers.sku === query) ?? false;

  const {
    data: results,
    isLoading,
    isError,
    refetch,
  } = useSearchProducts(hasCachedParent ? null : query);

  const parent = useMemo(() => {
    if (hasCachedParent) {
      return cachedParent.current;
    }
    const found = findParentByVariantSku(results, query);
    if (found) {
      cachedParent.current = found;
    }
    return found;
  }, [results, query, hasCachedParent]);

  const selectedVariantSku = query;

  const variant = useMemo<Variant | null>(
    () => parent?.variants.find((v) => v.identifiers.sku === selectedVariantSku) ?? null,
    [parent, selectedVariantSku],
  );

  return {
    parent,
    variant,
    selectedVariantSku,
    isLoading,
    isError,
    refetch,
    notFound: !query || (results !== undefined && !parent),
  };
}
